import { useCallback, useEffect } from 'react';
import format from 'date-fns/format';
import { usePathname, useRouter } from '@/i18n/routing';
import { useSearchParams } from 'next/navigation';

export const useUpdateQueryDates = () => {
  const { replace } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return useCallback(
    (dates) => {
      const query = {};

      searchParams.forEach((value, key) => (query[key] = value));

      return replace(
        {
          pathname: pathname,
          query: {
            ...query,
            startDate: dates.startDate,
            endDate: dates.endDate,
          },
        },
        { scroll: false, shallow: true  },
      );
    },
    [replace, pathname, searchParams]
  );
};

const fmt = (d) => format(d, 'yyyy-MM-dd');

export const useWatchDateQuery = ({
  startValue,
  endValue,
  onCanUpdate,
  delay = 500,
  startTransition
}) => {
  const searchParams = useSearchParams();
  const onUpdateQueryDates = useUpdateQueryDates();

  useEffect(() => {
    let timer = null;
    if (!startValue || !endValue || !searchParams.get('startDate')) return;

    const start = typeof startValue === 'string' ? startValue : fmt(startValue);
    const end = typeof endValue === 'string' ? endValue : fmt(endValue);

    if (
      start === searchParams.get('startDate') &&
      end === searchParams.get('endDate')
    ) {
      return;
    }

    const canUpdate = typeof onCanUpdate === 'function' ? onCanUpdate() : true;
    if (canUpdate) {
      timer = setTimeout(() => {
        timer = null;
        if (typeof startTransition === 'function') {
          startTransition(() => {
            onUpdateQueryDates({ startDate: start, endDate: end });
          });
        } else {
          onUpdateQueryDates({ startDate: start, endDate: end });
        }
      }, delay);
    }
    return () => clearTimeout(timer);
  }, [onCanUpdate, startValue, endValue, searchParams, onUpdateQueryDates]);

  return { onUpdateQueryDates };
};
